import { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Legend,
} from 'recharts';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatTime, calcDuration } from '../utils/dateUtils';
import { exportToCSV, exportToPDF } from '../utils/exportUtils';
import StatusBadge from '../components/StatusBadge';

const todayStr = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const Reports = () => {
  const { canViewAllOffices } = useAuth();
  const isGlobal = canViewAllOffices();

  const [offices, setOffices] = useState([]);

  const [filters, setFilters] = useState({
    startDate: firstOfMonth(),
    endDate: todayStr(),
    status: '',
    purpose: '',
    officeId: '',
  });
  const [data, setData]       = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    if (isGlobal) {
      api.get('/offices').then(({ data }) => setOffices(data.offices || [])).catch(() => {});
    }
  }, [isGlobal]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters(f => ({ ...f, [name]: value }));
  };

  const quickRange = (range) => {
    const today = new Date();
    let start;
    if (range === 'today') { start = todayStr(); }
    else if (range === 'week') {
      const d = new Date(today); d.setDate(d.getDate() - 6);
      start = d.toISOString().slice(0, 10);
    } else if (range === 'month') { start = firstOfMonth(); }
    else if (range === '3months') {
      const d = new Date(today); d.setMonth(d.getMonth() - 3);
      start = d.toISOString().slice(0, 10);
    }
    setFilters(f => ({ ...f, startDate: start, endDate: todayStr() }));
  };

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(filters);
      [...params.keys()].forEach(k => !params.get(k) && params.delete(k));
      const { data: res } = await api.get(`/visits/export?${params}`);
      setData(res.data);

      // Compute summary stats — count PEOPLE via Group Count, not rows
      const headcount  = (r) => Number(r['Group Count']) || 1;
      const sumPeople  = (rows) => rows.reduce((s, r) => s + headcount(r), 0);

      const total      = sumPeople(res.data);
      const official   = sumPeople(res.data.filter(r => r['Purpose'] === 'Official'));
      const personal   = sumPeople(res.data.filter(r => r['Purpose'] === 'Personal'));
      const completed  = sumPeople(res.data.filter(r => r['Status'] === 'completed'));
      const active     = sumPeople(res.data.filter(r => r['Status'] === 'active'));

      // Daily count for chart
      const byDate = {};
      res.data.forEach(r => {
        const d = r['Date'];
        const n = headcount(r);
        if (!byDate[d]) byDate[d] = { date: d, Total: 0, Official: 0, Personal: 0 };
        byDate[d].Total += n;
        if (r['Purpose'] === 'Official') byDate[d].Official += n;
        else byDate[d].Personal += n;
      });
      const chartData = Object.values(byDate).sort((a, b) => new Date(a.date) - new Date(b.date));

      setSummary({ total, official, personal, completed, active, chartData });
      setFetched(true);
      toast.success(`${res.data.length} records loaded (${total} visitors)`);
    } catch {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleExportCSV = () => {
    if (!data.length) return toast.warning('No data to export');
    exportToCSV(data, `visitor-report-${filters.startDate}-to-${filters.endDate}`);
    toast.success('CSV exported!');
  };

  const handleExportPDF = () => {
    if (!data.length) return toast.warning('No data to export');
    exportToPDF(data, `visitor-report-${filters.startDate}-to-${filters.endDate}`);
    toast.success('PDF exported!');
  };

  return (
    <div className="container-fluid py-4 px-3 px-lg-4">
      {/* Header */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <h4 className="fw-bold mb-0">Reports & Analytics</h4>
          <p className="text-muted small mb-0">Generate audit-ready visitor reports with date-range filters</p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-outline-success fw-semibold" onClick={handleExportCSV} disabled={!fetched || !data.length}>
            <i className="bi bi-file-earmark-spreadsheet me-1"></i>Export CSV
          </button>
          <button className="btn btn-sm btn-outline-danger fw-semibold" onClick={handleExportPDF} disabled={!fetched || !data.length}>
            <i className="bi bi-file-earmark-pdf me-1"></i>Export PDF
          </button>
        </div>
      </div>

      {/* Filter Card */}
      <div className="card border-0 shadow-sm rounded-4 mb-4">
        <div className="card-body p-4">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-3">
              <label className="form-label fw-semibold small">Start Date</label>
              <input type="date" className="form-control" name="startDate" value={filters.startDate} onChange={handleChange} />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label fw-semibold small">End Date</label>
              <input type="date" className="form-control" name="endDate" value={filters.endDate} onChange={handleChange} />
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label fw-semibold small">Status</label>
              <select className="form-select" name="status" value={filters.status} onChange={handleChange}>
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label fw-semibold small">Purpose</label>
              <select className="form-select" name="purpose" value={filters.purpose} onChange={handleChange}>
                <option value="">All</option>
                <option value="Official">Official</option>
                <option value="Personal">Personal</option>
              </select>
            </div>
            {isGlobal && (
              <div className="col-12 col-md-3">
                <label className="form-label fw-semibold small">Office</label>
                <select className="form-select" name="officeId" value={filters.officeId} onChange={handleChange}>
                  <option value="">All Offices</option>
                  {offices.map(o => <option key={o._id} value={o._id}>{o.name} ({o.code})</option>)}
                </select>
              </div>
            )}
            <div className="col-12 col-md-2">
              <button className="btn btn-primary w-100 fw-semibold" onClick={fetchReport} disabled={loading}>
                {loading
                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Loading…</>
                  : <><i className="bi bi-search me-2"></i>Generate</>}
              </button>
            </div>
          </div>

          {/* Quick Range Buttons */}
          <div className="mt-3 d-flex gap-2 flex-wrap">
            <span className="small text-muted me-1 align-self-center">Quick:</span>
            {[['today', 'Today'], ['week', 'Last 7 Days'], ['month', 'This Month'], ['3months', 'Last 3 Months']].map(([k, label]) => (
              <button key={k} className="btn btn-sm btn-outline-secondary" onClick={() => quickRange(k)}>{label}</button>
            ))}
          </div>
        </div>
      </div>

      {fetched && summary && (
        <>
          {/* Summary Cards */}
          <div className="row g-3 mb-4">
            {[
              { label: 'Total Visits',    value: summary.total,     color: 'primary',   icon: 'bi-people' },
              { label: 'Official',        value: summary.official,  color: 'info',      icon: 'bi-briefcase' },
              { label: 'Personal',        value: summary.personal,  color: 'warning',   icon: 'bi-person-heart' },
              { label: 'Completed',       value: summary.completed, color: 'success',   icon: 'bi-check-circle' },
            ].map(({ label, value, color, icon }) => (
              <div key={label} className="col-6 col-lg-3">
                <div className={`card border-0 shadow-sm rounded-4 border-start border-4 border-${color}`}>
                  <div className="card-body p-3 d-flex align-items-center gap-3">
                    <div className={`rounded-3 bg-${color} bg-opacity-10 p-2`}>
                      <i className={`bi ${icon} text-${color} fs-5`}></i>
                    </div>
                    <div>
                      <div className={`fs-3 fw-bold text-${color} lh-1`}>{value}</div>
                      <div className="small text-muted">{label}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Charts */}
          {summary.chartData.length > 1 && (
            <div className="row g-4 mb-4">
              <div className="col-12 col-lg-7">
                <div className="card border-0 shadow-sm rounded-4">
                  <div className="card-header bg-transparent border-0 pt-4 px-4 pb-0">
                    <h6 className="fw-bold mb-0"><i className="bi bi-bar-chart-fill text-primary me-2"></i>Daily Visitor Volume</h6>
                  </div>
                  <div className="card-body p-4">
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={summary.chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                        <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,.1)' }} />
                        <Bar dataKey="Official" stackId="a" fill="#0d6efd" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="Personal" stackId="a" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                        <Legend formatter={v => <span style={{ fontSize: 11 }}>{v}</span>} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
              <div className="col-12 col-lg-5">
                <div className="card border-0 shadow-sm rounded-4">
                  <div className="card-header bg-transparent border-0 pt-4 px-4 pb-0">
                    <h6 className="fw-bold mb-0"><i className="bi bi-graph-up-arrow text-primary me-2"></i>Trend Line</h6>
                  </div>
                  <div className="card-body p-4">
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={summary.chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                        <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,.1)' }} />
                        <Line type="monotone" dataKey="Total" stroke="#0d6efd" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Data Table */}
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-header bg-transparent border-0 pt-4 px-4 pb-0 d-flex align-items-center justify-content-between">
              <h6 className="fw-bold mb-0">
                <i className="bi bi-table text-primary me-2"></i>Report Data
                <span className="badge bg-primary ms-2">{data.length}</span>
              </h6>
              <div className="d-flex gap-2">
                <button className="btn btn-sm btn-outline-success" onClick={handleExportCSV}>
                  <i className="bi bi-download me-1"></i>CSV
                </button>
                <button className="btn btn-sm btn-outline-danger" onClick={handleExportPDF}>
                  <i className="bi bi-download me-1"></i>PDF
                </button>
              </div>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0 small">
                  <thead className="table-light">
                    <tr>
                      <th className="ps-4 py-3">Serial No.</th>
                      <th>Date</th>
                      <th>Visitor Name</th>
                      <th>Mobile</th>
                      <th>Meeting</th>
                      <th>Dept.</th>
                      <th>Purpose</th>
                      <th>Time In</th>
                      <th>Time Out</th>
                      <th className="pe-4">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row, i) => (
                      <tr key={i}>
                        <td className="ps-4 font-monospace text-primary fw-semibold">{row['Serial No']}</td>
                        <td className="text-muted">{row['Date']}</td>
                        <td className="fw-semibold">{row['Visitor Name']}</td>
                        <td className="font-monospace text-muted">{row['Mobile Number']}</td>
                        <td>{row['To Whom Meet']}</td>
                        <td className="text-muted">{row['Department'] || '—'}</td>
                        <td>
                          <span className={`badge ${row['Purpose'] === 'Official' ? 'bg-primary' : 'bg-warning text-dark'}`}>
                            {row['Purpose']}
                          </span>
                        </td>
                        <td>{row['Time In']}</td>
                        <td className={row['Time Out'] === 'Still Inside' ? 'text-success fw-semibold fst-italic' : ''}>{row['Time Out']}</td>
                        <td className="pe-4">
                          <StatusBadge status={row['Status']} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {!fetched && (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-bar-chart-line fs-1 d-block opacity-25 mb-3"></i>
          <p className="mb-1 fw-semibold">No report generated yet</p>
          <p className="small">Select a date range and click <strong>Generate</strong> to view the report.</p>
        </div>
      )}
    </div>
  );
};

export default Reports;
